/**
 * Category glyphs, drawn rather than borrowed.
 *
 * No general icon set has an honest mark for a medical specialty (lucide has
 * no tooth at all), and substituting a smiley for "dentist" or an apple for
 * "nutrition" is what makes a directory look like clip art.
 *
 * What makes a set read as professional is restraint, not detail. Each glyph
 * here is built from as few elements as the idea allows, on one spec:
 *   - 24x24 box, 1.5 stroke, round caps, no fills
 *   - the instrument or the subject, never a character or a mascot
 *   - straight lines, true circles and single arcs only
 *   - currentColor throughout, so the caller sets the colour
 *
 * Matching is by **keyword** against the admin-curated category list (H7), in
 * `categoryGlyph.ts` and unit-tested there, so "Dentist", "Dental" and "Dental
 * clinic" all find the tooth and an unanticipated category still lands on a
 * neutral clinical mark rather than rendering blank.
 */

import { categoryGlyph, type GlyphKey } from "./categoryGlyph";

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Dentist: a molar. */
function Tooth() {
  return (
    <path d="M8.2 3.6c-2 0-3.4 1.5-3.4 3.7 0 1.4.4 2.4.7 3.6.3 1.3.4 2.5.5 3.9.2 1.5.5 3.9 1.8 3.9 1.2 0 1.3-2.1 1.5-3.6.2-1.1.4-1.9 1.1-1.9s.9.8 1.1 1.9c.2 1.5.3 3.6 1.5 3.6 1.3 0 1.6-2.4 1.8-3.9.1-1.4.2-2.6.5-3.9.3-1.2.7-2.2.7-3.6 0-2.2-1.4-3.7-3.4-3.7-1.3 0-1.8.5-2.2.5s-.9-.5-2.2-.5Z" />
  );
}

/**
 * Chiropractic: a spine, seen from behind.
 *
 * This was the profile spine physiotherapy used to wear, three vertebrae
 * touching a curved column, and it was shared by chiropractic and osteopathy
 * too. Three specialties on one drawing is a directory telling people the
 * categories are the same thing, which is precisely what a chiropractor and a
 * physiotherapist would both say they are not.
 *
 * Seen from behind, the spine is chiropractic's own mark. Four vertebrae, not
 * five (five became a texture at 24px), and each one carries its transverse
 * processes as two short wings. The wings are the whole idea: without them the
 * bodies are a stack of pills, with them they are bones.
 */
function Chiro() {
  return (
    <>
      <rect x="9.4" y="2.6" width="5.2" height="2.9" rx="1" />
      <path d="M9.4 4 7.2 5M14.6 4l2.2 1" />
      <rect x="9.8" y="7.4" width="5.2" height="2.9" rx="1" />
      <path d="M9.8 8.8 7.6 9.8M15 8.8l2.2 1" />
      <rect x="9.8" y="12.2" width="5.2" height="2.9" rx="1" />
      <path d="M9.8 13.6 7.6 14.6M15 13.6l2.2 1" />
      <rect x="9.4" y="17" width="5.2" height="2.9" rx="1" />
      <path d="M9.4 18.4 7.2 19.4M14.6 18.4l2.2 1" />
    </>
  );
}

/**
 * Physiotherapy: a body in a lunge, and the arc of its range.
 *
 * Physiotherapy is movement, and the spine said bones. A figure mid-stretch was
 * rejected once for reading as a fitness app; what changes it here is the arc
 * beside it, the range-of-motion mark physiotherapy charts are drawn with, so
 * the glyph says "movement, measured" rather than "workout". A bent leg alone
 * and a knee drawn as two bones both lost to it at 24px: the leg read as a
 * letter, the knee as a crutch.
 */
function Physio() {
  return (
    <>
      <circle cx="9" cy="4.2" r="1.9" />
      <path d="M9 7.4 10.6 13l4.6.4 1.2 6.6" />
      <path d="M10.6 13 7 16.2l-2.4 4" />
      <path d="M9.6 9.4 4.6 8.4M9.6 9.4l5-2.6" />
      <path d="M18.4 9.4a7 7 0 0 1 1.2 5" />
    </>
  );
}

/**
 * Osteopathy: a bone.
 *
 * Osteopathy is named for bone, and a long bone with its two knuckled ends is
 * the one silhouette nothing else shares. Two hands cupping a cross was tried
 * and read as health insurance; a single-line bone with round ends read as a
 * dumbbell, which is why the shaft here is two lines.
 */
function Osteo() {
  return (
    <>
      <path d="M8.2 14.4 14.4 8.2M9.6 15.8l6.2-6.2" />
      <path d="M14.4 8.2a2 2 0 1 1 2.8-2.8 2 2 0 1 1 1.4 3 2 2 0 1 1-2.8 1.4" />
      <path d="M9.6 15.8a2 2 0 1 1-2.8 2.8 2 2 0 1 1-1.4-3 2 2 0 1 1 2.8-1.4" />
    </>
  );
}

/**
 * Massage: stacked stones, and a leaf.
 *
 * The flat stones and the leaf beside them are what massage rooms and their
 * signage already use. Two things were learned by rendering it: the stones
 * have to be flat ellipses with daylight between them, because rounded stones
 * touching read as an emoji nobody wants on a clinic tile; and the leaf has to
 * sit to one side, because a sprout on top read as the same thing. A hand with
 * rising lines read as a hot dish.
 */
function Massage() {
  return (
    <>
      <ellipse cx="12" cy="18.4" rx="8.2" ry="2.2" />
      <ellipse cx="12.4" cy="13.4" rx="5.8" ry="1.9" />
      <ellipse cx="11.8" cy="9" rx="3.6" ry="1.6" />
      <path d="M17.6 3.6c-2.6 0-4 1.4-4 3.2 2.4 0 4-1.2 4-3.2Z" />
    </>
  );
}

/**
 * Psychology: a head in profile, holding a heart.
 *
 * Two speech bubbles were the other candidate, and they read as messaging. The
 * head says the work is about the mind, the heart says it is about how someone
 * feels, and together they stop short of a brain, which at 24px is a tangle.
 */
function Psych() {
  return (
    <>
      <path d="M14.6 21v-3.4h2.2a1.6 1.6 0 0 0 1.6-1.6v-2.4l1.6-.6-1.6-3.4A7.2 7.2 0 0 0 4.2 9.4c0 2.6 1 4.4 2.6 5.8V21" />
      <path d="M11.2 13.2s-3-1.7-3-3.6a1.6 1.6 0 0 1 3-.8 1.6 1.6 0 0 1 3 .8c0 1.9-3 3.6-3 3.6Z" />
    </>
  );
}

/**
 * Dermatology: a hand, marked.
 *
 * The magnifier this replaces was the whole problem: a lens over anything reads
 * as "search" first and as a specialty second, and the dots that were meant to
 * say "skin" vanished at 16px. Cross-sections of the epidermis read as water,
 * a bathtub or a sandwich depending on how they were bounded, and a face in
 * profile did not read as a face at all.
 *
 * So the glyph shows skin on something the eye already recognises. A hand is
 * unmistakable at any size, and the marks on it are the reason someone books a
 * dermatologist. Marks on skin is also what dermatological pictograms
 * converge on once the magnifier is taken away.
 */
function Derm() {
  return (
    <>
      <path d="M8.8 12.6V5.8a1.5 1.5 0 0 1 3 0v4.6" />
      <path d="M11.8 10.4V4.6a1.5 1.5 0 0 1 3 0v5.8" />
      <path d="M14.8 10.8V6.8a1.5 1.5 0 0 1 3 0v7.8a6.2 6.2 0 0 1-6.2 6.2h-1a5 5 0 0 1-3.8-1.8L4 15.6a1.6 1.6 0 0 1 2.4-2.1l1.6 1.7" />
      <circle cx="11.2" cy="14.8" r="0.85" />
      <circle cx="14.4" cy="16.4" r="0.85" />
      <circle cx="10.8" cy="18.2" r="0.85" />
    </>
  );
}

/**
 * Nutrition: a bowl, and a leaf.
 *
 * The divided plate this replaces was right in theory (MyPlate is the standard
 * in dietary guidance) and unreadable in practice: seen from above, a circle
 * cut by two lines is a pie chart, and the cup beside it read as a stray dot.
 *
 * Seen from the side a bowl is unmistakably a bowl, and the leaf resting in it
 * is what separates eating well from eating out. No apple, no carrot, nothing
 * with a face.
 */
function Nutrition() {
  return (
    <>
      <path d="M2.6 12.8h18.8a9.4 7.6 0 0 1-18.8 0Z" />
      <path d="M12.4 11c-3.4 0-5.6-2.2-5.6-5.6 3.4 0 5.6 2.2 5.6 5.6Z" />
      <path d="M8.2 6.8 12.4 11" />
    </>
  );
}

/** Optician: spectacles. */
function Optician() {
  return (
    <>
      <circle cx="6.2" cy="14" r="3.4" />
      <circle cx="17.8" cy="14" r="3.4" />
      <path d="M9.6 13.6c.9-.7 3.9-.7 4.8 0" />
      <path d="M2.8 12.2 4.6 8.2h2.1" />
      <path d="M21.2 12.2 19.4 8.2h-2.1" />
    </>
  );
}

/** General practice: a stethoscope. */
function General() {
  return (
    <>
      <path d="M5.5 3.6v4.6a4.3 4.3 0 0 0 8.6 0V3.6" />
      <path d="M4 3.6h3M12.6 3.6h3" />
      <path d="M9.8 12.5v2.8a3.8 3.8 0 0 0 7.6 0v-1.1" />
      <circle cx="17.4" cy="11.6" r="2" />
    </>
  );
}

const GLYPHS: Record<GlyphKey, () => React.JSX.Element> = {
  tooth: Tooth,
  chiro: Chiro,
  physio: Physio,
  osteo: Osteo,
  massage: Massage,
  psych: Psych,
  derm: Derm,
  nutrition: Nutrition,
  optician: Optician,
  general: General,
};

export function CategoryIcon({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  const Glyph = GLYPHS[categoryGlyph(name)];
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden {...STROKE}>
      <Glyph />
    </svg>
  );
}
